import { useState, useEffect, useRef } from "react";
import { assetUrl, api, downloadUrl, exportUrl } from "@/lib/api";
import { DownloadButton, IconDownload } from "@/components/DownloadButton";
import { VideoDownloadMenu } from "@/components/VideoDownloadMenu";
import { NarrationEditor } from "@/components/NarrationEditor";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Play, Pause, Save, Film, Subtitles, Mic2, Loader2, ImageOff, Clapperboard, Package, Volume2,
} from "lucide-react";
import { toast } from "sonner";

export const MovieStudio = ({ project, refresh }) => {
  const movie = project.movie;
  const [active, setActive] = useState(0);
  const [script, setScript] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSub, setShowSub] = useState(true);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [film, setFilm] = useState(false);

  const scenes = movie?.scenes || [];
  const scene = scenes[active];

  useEffect(() => {
    setScript(scene?.script || "");
    setPlaying(false);
  }, [active, scene?.script]);

  useEffect(() => {
    if (!film || !scene || scene.audio_asset_id) return;
    const t = setTimeout(() => advance(), 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [film, active]);

  const advance = () => {
    if (film && active < scenes.length - 1) setActive((a) => a + 1);
    else { setFilm(false); setPlaying(false); }
  };

  if (!movie) return <Empty project={project} label="Movie pipeline" icon={Film} />;

  const saveScript = async () => {
    setSaving(true);
    await api.updateScene(project.id, active, { script });
    await refresh();
    setSaving(false);
    toast.success(`Scene ${active + 1} script saved`);
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setFilm(false); return; }
    a.play().catch(() => toast.error("Your browser blocked playback. Check your volume and try again."));
  };

  const playFilm = () => {
    if (film) { audioRef.current?.pause(); setFilm(false); return; }
    setFilm(true);
    if (active === 0 && audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
    else setActive(0);
  };

  const ready = project.status !== "processing";
  const name = (movie.title || "movie").replace(/[\\/:*?"<>|]+/g, "").trim() || "movie";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">{movie.title}</h2>
        {movie.logline && <p className="text-[#94A3B8] mt-1 italic">{movie.logline}</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          <button data-testid="play-full-film-btn" onClick={playFilm} className="inline-flex items-center gap-2 rounded-full bg-[#3B82F6] text-white font-semibold text-sm px-4 py-2 hover:bg-[#2563EB] transition-colors">
            {film ? <Pause className="h-4 w-4" /> : <Clapperboard className="h-4 w-4" />} {film ? "Stop film" : "Play full film with voice"}
          </button>
          <VideoDownloadMenu projectId={project.id} name={name} disabled={!ready} />
          <DownloadButton testid="download-movie-pack-btn" href={exportUrl(project.id, "movie-pack")} filename="movie-pack.zip" label="Frames + voice (.zip)" icon={Package} disabled={!ready} />
        </div>
      </div>

      {/* Main frame */}
      <div className="rounded-2xl overflow-hidden bg-black border border-white/10">
        <div className="relative aspect-video bg-[#0F1117] flex items-center justify-center">
          {scene?.image_asset_id ? (
            <img data-testid="movie-frame-img" src={assetUrl(scene.image_asset_id)} alt={scene.heading} className="h-full w-full object-cover" />
          ) : project.status === "processing" ? (
            <div className="flex flex-col items-center gap-2 text-[#94A3B8]"><Loader2 className="h-7 w-7 animate-spin text-[#3B82F6]" /><span className="text-sm">Rendering frame…</span></div>
          ) : (
            <ImageOff className="h-10 w-10 text-[#334155]" />
          )}
          {/* subtitle overlay */}
          {showSub && scene?.subtitle && (
            <div data-testid="subtitle-overlay" className="absolute bottom-5 left-1/2 -translate-x-1/2 max-w-[80%] text-center">
              <span className="bg-black/70 text-white px-4 py-1.5 rounded text-sm sm:text-base font-serif-i leading-snug">{scene.subtitle}</span>
            </div>
          )}
          {scene?.image_asset_id && (
            <div className="absolute top-3 right-3"><IconDownload testid="download-frame-btn" title="Download frame" href={downloadUrl(scene.image_asset_id, `scene-${scene.number}-frame`)} /></div>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <Badge className="bg-[#3B82F6]/90 text-white border-0"><Film className="h-3 w-3 mr-1" /> Scene {scene?.number}</Badge>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 px-4 py-3 bg-[#1E293B] border-t border-white/10">
          <button data-testid="voiceover-play-btn" onClick={togglePlay} disabled={!scene?.audio_asset_id} className="h-10 w-10 rounded-full bg-[#22C55E] text-[#0F1117] flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform">
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </button>
          <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
            <Mic2 className="h-4 w-4 text-[#22C55E]" />
            {scene?.audio_asset_id ? "AI voiceover ready" : project.status === "processing" ? "Synthesizing…" : "No voiceover"}
          </div>
          {scene?.audio_asset_id && (
            <a data-testid="download-voiceover-btn" href={downloadUrl(scene.audio_asset_id, `scene-${scene.number}-voiceover`)} download className="inline-flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#22C55E]">
              <Volume2 className="h-3.5 w-3.5" /> Save voice clip
            </a>
          )}
          <div className="ml-auto">
            <button data-testid="subtitle-toggle-btn" onClick={() => setShowSub((s) => !s)} className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 transition-colors ${showSub ? "bg-[#3B82F6]/20 text-[#3B82F6]" : "bg-white/5 text-[#94A3B8]"}`}>
              <Subtitles className="h-3.5 w-3.5" /> Subtitles {showSub ? "On" : "Off"}
            </button>
          </div>
          {scene?.audio_asset_id && (
            <audio key={scene.audio_asset_id} ref={audioRef} src={assetUrl(scene.audio_asset_id)} autoPlay={film} preload="auto"
              onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={advance}
              onError={() => toast.error("Couldn't load this voiceover")} className="hidden" />
          )}
        </div>
      </div>

      {/* Timeline */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#94A3B8] mb-3">Scene Timeline</div>
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="scene-timeline">
          {scenes.map((s, i) => (
            <button
              key={i}
              data-testid={`timeline-scene-${i}`}
              onClick={() => { setFilm(false); setActive(i); }}
              className={`relative shrink-0 w-40 rounded-lg overflow-hidden border-2 transition-all ${active === i ? "border-[#3B82F6]" : "border-transparent opacity-70 hover:opacity-100"}`}
            >
              <div className="aspect-video bg-[#0F1117] flex items-center justify-center">
                {s.image_asset_id ? (
                  <img src={assetUrl(s.image_asset_id)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-[#334155] text-xs">Scene {s.number}</span>
                )}
              </div>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-left">
                <span className="text-[10px] text-white line-clamp-1">{s.heading}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Script editor */}
      <div className="glass rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">Scene {scene?.number} Script — <span className="text-[#94A3B8] font-normal">{scene?.heading}</span></div>
          <Button data-testid="save-script-btn" onClick={saveScript} disabled={saving} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
          </Button>
        </div>
        <Textarea
          data-testid="script-editor"
          value={script}
          onChange={(e) => setScript(e.target.value)}
          className="min-h-[140px] bg-[#0F1117]/70 border-white/10 font-mono text-sm resize-none"
        />
        {scene && (
          <NarrationEditor
            key={`${active}-${scene.narration}`}
            project={project}
            sceneIndex={active}
            narration={scene.narration || ""}
            onSaved={async () => {
              await refresh();
              setTimeout(() => audioRef.current?.play().catch(() => {}), 300);
            }}
          />
        )}
      </div>
    </div>
  );
};

const Empty = ({ project, label, icon: Icon }) => {
  if (project?.status === "processing")
    return (
      <div className="glass rounded-2xl py-20 text-center text-[#94A3B8]">
        <Loader2 className="h-8 w-8 mx-auto text-[#3B82F6] animate-spin mb-3" />
        {label} is being generated… assets will appear here shortly.
      </div>
    );
  if (project?.status === "error")
    return <div className="glass rounded-2xl py-20 text-center text-red-300">{label} failed to generate. See the developer drawer logs.</div>;
  return (
    <div className="glass rounded-2xl py-20 text-center text-[#94A3B8]">
      {Icon && <Icon className="h-8 w-8 mx-auto text-[#334155] mb-3" />}
      {label} is not available for this project.
    </div>
  );
};
